import { publicAxios as axios } from "../../config/axios";
import endpoints from "../../utils/endpoints";
import globalConstant from "../../utils/constants";
import PlaceOrderServices from "../PlaceOrderTest/services";
import OrderServices from "../Orders/services";
import { parseConfigArray } from "../../utils/util";
import {
  parseTestPrefillConfigRaw,
  type TestPrefillConfig,
} from "../PlaceOrderTest/testPrefillConfig";

const authHeaders = () => ({
  Authorization: localStorage.getItem(globalConstant.AUTH_TOKEN),
});

export interface TestCustomerOption {
  _id: string;
  name: string;
  code?: string;
  mobileNumber?: string;
  isTestCustomer: boolean;
  addresses?: any[];
  billingAddress?: any;
  gstin?: string;
  raw: any;
}

export interface TestSupplierOption {
  _id: string;
  name: string;
  businessName?: string;
  mobileNumber?: string;
  isTestSupplier: boolean;
  raw: any;
}

const toCustomerOption = (c: any): TestCustomerOption => ({
  _id: String(c._id),
  name: c.name || c.businessName || "Unnamed customer",
  code: c.code,
  mobileNumber: c.mobileNumber,
  isTestCustomer: Boolean(c.isTestCustomer),
  addresses: c.addresses,
  billingAddress: c.billingAddress,
  gstin: c.gstin,
  raw: c,
});

const toSupplierOption = (s: any): TestSupplierOption => ({
  _id: String(s._id),
  name: s.businessName || s.name || "Unnamed supplier",
  businessName: s.businessName,
  mobileNumber: s.user?.mobileNumber || s.mobileNumber,
  isTestSupplier: Boolean(s.isTestSupplier),
  raw: s,
});

/**
 * Data access for the flow tester's configuration UI. Production safety:
 * customer and supplier lookups only ever return records flagged as test
 * entities; nothing here can surface a production customer/supplier.
 */
class TestOrderServices {
  async searchTestCustomers(search: string): Promise<TestCustomerOption[]> {
    const trimmed = search.trim();
    if (!trimmed) return [];
    const data = await PlaceOrderServices.getXpressCustomer(
      encodeURIComponent(trimmed)
    );
    const list: any[] = Array.isArray(data) ? data : [];
    return list.filter((c) => c?.isTestCustomer === true).map(toCustomerOption);
  }

  async getTestCustomerById(customerId: string): Promise<TestCustomerOption | null> {
    if (!customerId) return null;
    const customer = await PlaceOrderServices.getCustomerForPrefill(customerId);
    if (!customer || customer.isTestCustomer !== true) return null;
    return toCustomerOption(customer);
  }

  async getTestSuppliers(): Promise<TestSupplierOption[]> {
    const suppliers: any[] = await OrderServices.getAllSuppliers();
    return (suppliers || [])
      .filter((s) => s?.isTestSupplier === true)
      .map(toSupplierOption);
  }

  async getSupplierDetails(supplierId: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: `/v1/suppliers/${supplierId}`,
      headers: authHeaders(),
    });
    return response.data;
  }

  async getMobileConfig(): Promise<Record<string, string>> {
    return PlaceOrderServices.getOrderMediaConfig();
  }

  getPrefillConfig(mobileConfig?: Record<string, string> | null): TestPrefillConfig | null {
    return parseTestPrefillConfigRaw(mobileConfig?.orca_test_order_prefill_config);
  }

  getAutonomousSupplierIds(mobileConfig?: Record<string, string> | null): string[] {
    return parseConfigArray(mobileConfig?.autonomous_supplier_ids, true);
  }

  getPartConfirmationEligibleSupplierIds(
    mobileConfig?: Record<string, string> | null
  ): string[] {
    return parseConfigArray(
      mobileConfig?.part_confirmation_eligible_supplier_ids,
      true
    );
  }

  async getCarMakers() {
    return PlaceOrderServices.getCarMakers();
  }

  async searchParts(query: string, brand?: string) {
    return PlaceOrderServices.searchPartsForPrefill(query, brand);
  }

  async resolvePart(
    partId: string,
    options?: { partNumber?: string; partName?: string; carMaker?: string }
  ) {
    return PlaceOrderServices.resolvePartForPrefill(partId, options);
  }

  async uploadMedia(file: File): Promise<{ _id: string; path: string; mimeType: string }[]> {
    const formData = new FormData();
    formData.append("media", file);
    return PlaceOrderServices.addMedia(formData);
  }

  async getOrderById(orderId: string) {
    const response = await axios({
      method: globalConstant.GET,
      url: endpoints.getGaraazOrder(orderId),
      headers: authHeaders(),
    });
    return response.data;
  }
}

const testOrderServices = new TestOrderServices();
export default testOrderServices;
